import { useEffect, useState } from 'react'

import './_accountSettings.scss'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'

import InputCard from 'components/Form/InputCard/InputCard'
import FixedBottomButton from 'components/UI/FixedBottomButton/FixedBottomButton'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { SuccessfulChange } from 'pages/settings/features/SuccessfulChange'
import { editProfileInfo } from 'services/endpoints/settings/account/profileSettings'
import { useSetUserProfile, useUserProfile } from 'store'

export const ChangeDisplayName = () => {
    const [newDisplayName, setNewDisplayName] = useState<string>('')
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [usernameTaken, setUsernameTaken] = useState<boolean>(false)
    const [name, setName] = useState<string>('')
    const [isDirty, setIsDirty] = useState<boolean>(false)

    const userData = useUserProfile()
    const navigate = useNavigate()
    const { t } = useTranslation()

    useEffect(() => {
        if (userData) {
            setNewDisplayName(userData.display_name)
            setName(userData.display_name)
        }
    }, [userData])

    useEffect(() => {
        name !== newDisplayName ? setIsDirty(true) : setIsDirty(false)
    }, [name, newDisplayName])

    const updateDisplayName = useMutation(
        async (newName: { display_name: string }) =>
            await editProfileInfo(
                { display_name: newName.display_name },
                'display-name'
            ),
        {
            onError: err => {
                console.error(err)
                setUsernameTaken(true)
            },
            onSuccess: () => {
                useSetUserProfile({
                    display_name: newDisplayName
                })

                setName(newDisplayName)
                setIsDirty(false)
                setCurrentPage(2)
            }
        }
    )

    const renderCurrentPage = () => {
        switch (currentPage) {
            case 1:
                return (
                    <InputCard
                        hasAvatar={true}
                        type='text'
                        label={t('displayName')}
                        value={newDisplayName}
                        changeFn={(val: string) => {
                            setUsernameTaken(false)
                            setNewDisplayName(val)
                        }}
                        errorActive={usernameTaken}
                        errorMessage={t('error:thatNameIsTaken')}
                        validate='letters/numbers'
                    />
                )

            case 2:
                return (
                    <SuccessfulChange
                        img={Icons.avatar_big}
                        text={t('yourDisplayNameHasSuccessfullyChanged')}
                    />
                )
        }
    }

    const renderBottomButton = () => {
        switch (currentPage) {
            case 1:
                return (
                    <FixedBottomButton
                        customClass='accountsettings__card__fixedbottombtn'
                        text={t('update')}
                        disabled={
                            !isDirty ||
                            (newDisplayName.length < 6 &&
                                !/^[a-zA-Z0-9._]*$/.test(newDisplayName))
                        }
                        clickFn={() => {
                            updateDisplayName.mutate({
                                display_name: newDisplayName
                            })
                        }}
                    />
                )

            case 2:
                return (
                    <FixedBottomButton
                        customClass='accountsettings__card__fixedbottombtn'
                        text={t('close')}
                        clickFn={() => {
                            navigate('/settings/account')
                        }}
                    />
                )
        }
    }

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('account')}
                    title={t('changeDisplayName')}
                />
            }
        >
            <>
                {renderCurrentPage()}
                {renderBottomButton()}
            </>
        </WithHeaderSection>
    )
}
